import { FieldType, ViewModelBase } from "@shoothill/core";
import { action, computed, observable, runInAction } from "mobx";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import moment from "moment";
import { AppUrls } from "AppUrls";
import validator from "validator";

// View models
import FormSectionViewModelBase from "Globals/ViewModels/FormSections/FormSectionViewModelBase";
import MoneyGifterViewModel from "Globals/ViewModels/FormSections/IncomeSavingsAndOutgoings/MoneyGifterViewModel";

// Models
import { StatementDetailsModel } from "Globals/Models/Domain/FormSections/StatmentDetails/StatementDetailsModel";
import { StatementDetailDTO } from "Globals/Models/DTO/StatementDetailDTO";
import { FormSectionsEnum } from "Globals/Models/FormSectionsEnum";

export default class StatementDetailsViewModel extends FormSectionViewModelBase<StatementDetailsModel> {
    @observable public isSectionExpanded: boolean = true; // TODO Link up with accordian

    @observable public moneyGifterViewModels: MoneyGifterViewModel[] = [];

    constructor(statementDetailsModel: StatementDetailsModel, _updateSectionCompleted: (index: FormSectionsEnum, value: boolean) => void) {
        super(_updateSectionCompleted, statementDetailsModel);
        this.saveStore.addCallback(FormSectionsEnum.StatementDetails, this.save);
        this.saveFunction = this.save;
        this.createViewModels();
        if (this.updateSectionCompleted) {
            this.updateSectionCompleted(FormSectionsEnum.StatementDetails, this.canProceed);
        }
    }

    @action
    private createViewModels(): void {
        // if (this.model.moneyGifterModels) {
        //     for (const model of this.model.moneyGifterModels) {
        //         this.moneyGifterViewModels.push(new MoneyGifterViewModel(model));
        //     }
        // }
    }

    @computed
    public get getSortedMoneyGifterViewModels(): MoneyGifterViewModel[] {
        return this.moneyGifterViewModels
            .slice()
            .sort((a, b) => (b.model.createdDate ? new Date(b.model.createdDate).getTime() : 0) - (a.model.createdDate ? new Date(a.model.createdDate).getTime() : 0));
    }

    public save = async (): Promise<void> => {
        try {
            let isMyModelValid: boolean = await this.isMyModelValid();
            let isMyModelDirty: boolean = this.isModelDirty;
            if (isMyModelValid && isMyModelDirty === true) {
                this.setIsLoading(true);
                this.saveStore.setIsSaving(true);
                let upsertStatementDetails: StatementDetailDTO = this.model.toDto();
                const apiResult = await this.Post<StatementDetailDTO>(AppUrls.Server.ApplicationForm.Upsert.StatementDetails, upsertStatementDetails);

                if (apiResult.wasSuccessful) {
                    this.model.fromStatementDetailDto(apiResult.payload);
                    console.info("StatementDetails:Upsert-Successful");
                    // Will set this section as completed when canProceed returns true
                    this.updateSectionCompleted(FormSectionsEnum.StatementDetails, this.canProceed);

                    this.setModelDirty(false);
                    return Promise.resolve();
                } else {
                    console.error("StatementDetails:Upsert-Error", apiResult.errors);
                    return Promise.reject(apiResult.errors);
                }
            }
        } catch (exception) {
            console.log("Error:", exception);
        } finally {
            this.setIsLoading(false);
            this.saveStore.setIsSaving(false);
            this.saveStore.setIsSaved(true);
        }
    };
    /**
     * Custom model validation function. Validates child view models.
     * @returns boolean If model is valid or not.
     */
    private isMyModelValid = async (): Promise<boolean> => {
        let isValid = true;
        if ((await this.isModelValid()) == false) {
            isValid = false;
        }
        return isValid;
    };

    /**
     * Clears conditional fields from the model.
     */
    private clearConditionalModelData(): void {
        if (this.model.hasRelationshipWithHHA == false) {
            this.model.relationshipTypeId = "";
        }
    }

    @action
    public handleInputChange = (): void => {
        this.clearConditionalModelData();
        this.saveStore.setIsSaved(false);
        this.restartTimer();
        this.saveFunction = this.save;
        this.updateSectionCompleted(FormSectionsEnum.StatementDetails, this.canProceed);
    };

    @action
    public handleCheckboxChange = (fieldName: keyof FieldType<StatementDetailsModel>, val: boolean): void => {
        this.model.setValue(fieldName, val);
        this.model.setDirty(fieldName, true);
        this.handleInputChange();
    };

    @action
    public handleFieldChange = (fieldName: keyof FieldType<StatementDetailsModel>, val: string): void => {
        this.model.setValue(fieldName, val);
        this.model.setDirty(fieldName, true);
        this.handleInputChange();
    };
    @action
    public handleRadioChange = (fieldName: keyof FieldType<StatementDetailsModel>, val: string): void => {
        this.model.setValue(fieldName, val == "1" ? true : false);
        this.model.setDirty(fieldName, true);
        this.handleInputChange();
    };

    @action
    public setDate(fieldName: keyof FieldType<StatementDetailsModel>, val: MaterialUiPickersDate) {
        if (val !== null) {
            this.setValue(fieldName, moment(val).utc().toISOString());
        } else {
            this.setValue(fieldName, val);
        }
        this.handleInputChange();
    }

    @computed
    public get canProceed(): boolean {
        let completed = true;
        Object.keys(this.model).forEach((_field) => {
            let field = _field as keyof FieldType<StatementDetailsModel>;
            let value = this.model.getValue(field);
            if (field == "relationshipTypeId") {
                if (this.model.hasRelationshipWithHHA == true) {
                    if (value == null || value == "") {
                        completed = false;
                    }
                }
            }
            if (completed == false) {
                console.error("Field not completed: ", field, "Value: ", value);
            }
        });
        return completed;
    }

    public async isFieldValid(fieldName: keyof FieldType<StatementDetailsModel>, value: any): Promise<boolean> {
        return await this.isValid(fieldName, value);
    }

    public async isValid(fieldName: keyof FieldType<StatementDetailsModel>, value: any, isRequired: boolean = false): Promise<boolean> {
        let { isValid, errorMessage } = await this.validateDecorators(fieldName);

        if (fieldName == "notes") {
            let allChecked = true;
            //console.log(this.model);

            if (this.model.hasActiveIVCA == true) allChecked = false;
            if (this.model.hasHadRepossession == true) allChecked = false;
            if (this.model.hasAccessToCosts == false) allChecked = false;
            if (this.model.hasLateLoanPayments == true) allChecked = false;
            if (this.model.hasLateHomePayments == true) allChecked = false;
            if (this.model.hasHadCCJ == true) allChecked = false;
            if (this.model.hasBeenBancrupt == true) allChecked = false;
            if (this.model.isReceivingHousingBenefit == true) allChecked = false;

            let isChecked = allChecked ? "Yes." : "No.";

            //console.log("All checked?");
            //console.log(isChecked);

            if (allChecked == false) {
                if (value == null || validator.isEmpty(value)) {
                    errorMessage = "You must provide a brief summary of why you are unable to check ALL boxes";
                    isValid = false;
                }
            }
        }

        if (fieldName == "relationshipTypeId") {
            //console.log("relationshipTypeId");
            //console.log(value);
            if (this.model.hasRelationshipWithHHA == true) {
                if (!value || value == "placeholder") {
                    errorMessage = "You must specify the nature of your relationship";
                    isValid = false;
                }
            }
        }

        if (fieldName == "hhaEmployeeName") {
            if (this.model.hasRelationshipWithHHA == true) {
                if (!value || value == "placeholder") {
                    errorMessage = "You must specify the employees name";
                    isValid = false;
                }
            }
        }

        if (isValid == false) {
            console.log("Field Not valid: ", fieldName);
        }
        this.setError(fieldName, errorMessage);
        this.setValid(fieldName, isValid);
        return isValid;
    }

    /**
     * @returns boolean does this section have any errors
     */
    @action
    public async showUnfinishedFields(): Promise<boolean> {
        let hasError = false;
        for (const _field of Object.keys(this.model)) {
            let field = _field as keyof FieldType<StatementDetailsModel>;
            let value = this.model.getValue(field);
            let isRequired = true;
            let isValid = await this.isValid(field, value, isRequired);
            if (isValid == false) {
                hasError = true;
            }
        }
        return hasError;
    }
    public beforeUpdate: undefined;
}
